import React, {
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cx from 'classnames';
import { components } from 'react-select';
import type {
  ClearIndicatorProps,
  GroupBase,
  MultiValue,
  MultiValueRemoveProps,
  SelectInstance,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type { CreatableProps } from 'react-select/creatable';

import type { Char } from 'src/types';

import FormControl from '../FormControl';
import { Close } from '../../Icons';

type Option = {
  readonly label: string;
  readonly value: string;
};

export interface MultiInputTextProps
  extends Omit<
    CreatableProps<Option, true, GroupBase<Option>>,
    'value' | 'onChange' | 'defaultValue'
  > {
  label?: string;
  placeholder?: string;
  inputClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  isError?: boolean;
  helperText?: ReactNode | string;
  className?: string;
  defaultValue?: string[];
  onInputChange?: (value: string) => void;
  onChange?: (option: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  isClearable?: boolean;
  delimiterChars?: Char[];
}

const MultiInputText = React.forwardRef<
  SelectInstance<MultiInputTextProps, true, GroupBase<MultiInputTextProps>>,
  MultiInputTextProps
>(
  (
    {
      defaultValue,
      className,
      inputClassName,
      label,
      placeholder,
      size,
      helperText,
      isError,
      required,
      disabled,
      readOnly,
      isClearable,
      onChange = () => {},
      onInputChange = () => {},
      onBlur = () => {},
      delimiterChars,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState<readonly Option[]>([]);

    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue.map(createOption));
      }
    }, [defaultValue]);

    const regEx = useMemo(
      () =>
        delimiterChars
          ? new RegExp(`(?:\\${delimiterChars.join('|\\')})+`, 'g')
          : null,
      [delimiterChars]
    );

    // * turn string label into option object
    const createOption = (label: string) => ({
      label,
      value: label,
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!inputValue) return;

      switch (event.key) {
        case 'Enter':
        case 'Tab':
          if (!value.find((it) => it.value === inputValue)) {
            const newValues = [...value, createOption(inputValue)];
            setValue(newValues);
            onChange(newValues.map(({ value }) => value));
          }
          setInputValue('');
          event.preventDefault();
      }
    };

    const handleOnBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
      onBlur(event);

      if (!inputValue) return;

      if (!value.find((it) => it.value === inputValue)) {
        const newValues = [...value, createOption(inputValue)];
        setValue(newValues);
        onChange(newValues.map(({ value }) => value));
      }
      setInputValue('');
      event.preventDefault();
    };

    const handleOnInputChange = (val: string) => {
      const newValArray = val.split(regEx);

      if (newValArray.length > 1) {
        const re = newValArray
          .filter((val) => val && !value.find((it) => it.value === val))
          .map(createOption);
        setValue((prev) => [...prev, ...re]);
        setInputValue('');
      } else {
        onInputChange?.(val);
        setInputValue(val);
      }
    };

    const handleOnChange = (val: MultiValue<Option>) => {
      if (readOnly) return;
      onChange(val.map(({ value }) => value));
      setValue(val);
    };

    const MultiValueRemove = (props: MultiValueRemoveProps) => (
      <components.MultiValueRemove {...props}>
        <Close className="cursor-pointer" width={16} height={16} />
      </components.MultiValueRemove>
    );

    const ClearIndicator = ({
      innerProps: { ref, ...restInnerProps },
      clearValue,
    }: ClearIndicatorProps<Option, true>) => (
      <div {...restInnerProps} ref={ref}>
        <Close
          className="cursor-pointer"
          width={16}
          height={16}
          onClick={(e) => {
            clearValue();
            e.preventDefault();
          }}
        />
      </div>
    );

    return (
      <FormControl
        required={required}
        disabled={disabled}
        error={isError}
        size={size}
        className={cx(className && className)}
      >
        <div className="flex items-center px-2 gap-2 w-full">
          <div
            className={cx('relative w-full flex flex-col justify-center', {
              'min-h-[40px]': size === 'sm',
              'min-h-[50px]': size === 'md',
              'min-h-[60px]': size === 'lg',
            })}
          >
            {label && <FormControl.Label>{label}</FormControl.Label>}
            <CreatableSelect
              {...props}
              className={cx(
                'multi-input-text',
                size,
                inputClassName && inputClassName
              )}
              classNamePrefix="multi-input"
              ref={ref}
              components={{
                MultiValueRemove,
                ClearIndicator,
                DropdownIndicator: null,
              }}
              inputValue={inputValue}
              isClearable={!readOnly && isClearable}
              isMulti
              menuIsOpen={false}
              onChange={handleOnChange}
              onInputChange={handleOnInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleOnBlur}
              placeholder={placeholder}
              value={value}
              isDisabled={disabled}
              isSearchable={!readOnly}
              styles={{
                control: (base, state) => ({
                  ...base,
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  minHeight: 'auto',
                  background: 'transparent',
                }),
                valueContainer: (base, state) => ({
                  ...base,
                  padding: '0',
                  gap: '0.3125rem',
                }),
                placeholder: (base, state) => ({
                  ...base,
                  margin: 0,
                  display:
                    (!placeholder || (label && !state.isFocused)) && 'none',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  marginTop: '-1rem',
                }),
                input: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                  margin: 0,
                }),
                multiValueRemove: (styles) => ({
                  ...styles,
                  height: '100%',
                  marginTop: 'auto',
                  marginBottom: 'auto',
                  backgroundColor: undefined,
                  ':hover': {
                    ...styles[':hover'],
                    backgroundColor: undefined,
                  },
                  ':focus': {
                    ...styles[':focus'],
                    backgroundColor: undefined,
                  },
                }),
              }}
            />
          </div>
        </div>
        {helperText && (
          <FormControl.HelperText>{helperText}</FormControl.HelperText>
        )}
      </FormControl>
    );
  }
);

MultiInputText.displayName = 'MultiInputText';
MultiInputText.defaultProps = {
  size: 'sm',
  required: false,
  isError: false,
  isClearable: false,
};

export default MultiInputText;
